/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * CheckInHistory — the History tab of /organizer/check-in
 * ------------------------------------------------------
 * The Scanner tab only shows events open for check-in right now, so a finished
 * event drops off it. This keeps its results: pick any of your events and see
 * how many registered, checked in, checked out or never arrived, and who.
 *
 * Requests: the event list is the cached query the Events page already uses,
 * so opening and searching the picker cost nothing. Picking an event makes
 * five small reads — the ids of its CONFIRMED registrations, two COUNT(*)s of
 * their checked-in and checked-out passes for the tiles, then the first 50
 * participants and their times (the participant total doubles as Registered).
 * Every pass read carries `registration_id IN (…)`: a cancelled registration
 * keeps its ISSUED pass, and must not count as Registered or Not arrived.
 * "Show more" is two more reads; a chip or a passcode search is two, plus one
 * count for Registered. Nothing refreshes in the background — Refresh does.
 *
 * Picking an activity narrows the participant list as well as the tiles: the
 * registration-id read is that activity's confirmed ones instead, plus one
 * COUNT(*) for its All chip once a chip or a search narrows the list.
 * Export (ExportParticipants) makes no request until a button is pressed.
 */

import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useOpenCheckInEvents, usePassPages, useDebounced, pageRows, checkInApi,
} from '../../../hooks/useCheckInDesk';
import {
  PASS_STATUSES, BREAKDOWN_QUERY, runAnalyticsQuery,
  fetchConfirmedRegistrationIds, countActivityPasses,
} from './checkInApi';
import ExportParticipants from './ExportParticipants';
import { groupEvents, defaultEventId, breakdownFromResult } from './checkInResults';
import ActivityPicker, { ALL_ACTIVITIES, hasChoice } from './ActivityPicker';
import ParticipantDetails from './ParticipantDetails';
import { clockIn } from './checkInRules';
import EventPicker from './EventPicker';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'checked_in', label: 'Checked in' },
  { id: 'checked_out', label: 'Checked out' },
  { id: 'not_arrived', label: 'Not arrived' },
];
const ROW_STATUS = {
  CHECKED_OUT: { label: 'Checked out', tone: 'out' },
  CHECKED_IN: { label: 'Checked in', tone: 'in' },
  ISSUED: { label: 'Not arrived', tone: 'none' },
};

/**
 * One event's totals, all from COUNT(*)s on the passes of its CONFIRMED
 * registrations (`registrationIds`): checked in and checked out are one each;
 * Registered is the unfiltered list's total when that list is showing, else
 * one more.
 *
 * Previously checked in/out were counted on event_check_in / event_check_out
 * and Registered on every non-cancelled pass — but a cancelled registration
 * keeps its ISSUED pass, so it stayed in Registered and Not arrived.
 */
function useEventTotals(eventId, list, listIsAll, registrationIds, regsKey, needed = true) {
  // `needed` is false while the list follows one activity: then every tile and
  // chip reads that activity's breakdown row, and these whole-event counts
  // would be read only to be ignored. They load when the view returns to the
  // whole event.
  const ids = eventId == null ? [] : [eventId];
  const ready = eventId != null && registrationIds != null && needed;
  const counts = useQuery({
    queryKey: ['checkInHistory', eventId, 'counts', regsKey],
    queryFn: async () => {
      const [checkedIn, checkedOut] = await Promise.all([
        countActivityPasses(checkInApi, ids, PASS_STATUSES.checked_in, registrationIds),
        countActivityPasses(checkInApi, ids, PASS_STATUSES.checked_out, registrationIds),
      ]);
      return { checkedIn, checkedOut };
    },
    enabled: ready,
    staleTime: Infinity,
  });
  const registered = useQuery({
    queryKey: ['checkInHistory', eventId, 'registered', regsKey],
    queryFn: () => countActivityPasses(checkInApi, ids, PASS_STATUSES.all, registrationIds),
    enabled: ready && !listIsAll,
    staleTime: Infinity,
  });
  const total = listIsAll ? list.data?.pages[0]?.count : registered.data;
  if (!counts.data || total == null) return { totals: null, counts, registered };
  const { checkedIn, checkedOut } = counts.data;
  return {
    counts,
    registered,
    totals: {
      registered: total,
      checkedIn,
      checkedOut,
      stillIn: Math.max(0, checkedIn - checkedOut),
      notArrived: Math.max(0, total - checkedIn),
    },
  };
}

/**
 * Every per-activity number for the picked event, from the saved analytics
 * query `event_activity_breakdown`.
 *
 * One request, one row per activity. The request carries the query's NAME and
 * the event id — no SQL, no table name — and the server applies the
 * organizer's own row-level security before it runs.
 *
 * ASKED SEPARATELY FROM THE RESULTS, and its failure is deliberately kept out
 * of the page's `failed` flag: an organizer looking at who arrived is not
 * helped by losing that list because a supplementary breakdown could not be
 * read.
 */
function useActivityBreakdown(eventId) {
  const query = useQuery({
    queryKey: ['checkInHistory', eventId, 'breakdown'],
    queryFn: () => runAnalyticsQuery(checkInApi, BREAKDOWN_QUERY, { event_id: eventId }),
    enabled: eventId != null,
    // These numbers change when someone registers or scans, not on a timer.
    // Refresh re-runs it; nothing else does.
    staleTime: 5 * 60 * 1000,
  });
  const breakdown = useMemo(() => breakdownFromResult(query.data), [query.data]);
  return { breakdownQuery: query, breakdown };
}

function CheckInHistory() {
  const desk = useOpenCheckInEvents();
  const groups = useMemo(() => groupEvents(desk.events, desk.now), [desk.events, desk.now]);
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(null); // the row whose details dialog is open
  const passcode = useDebounced(search.trim());

  // The picked event lives in the URL, like the tab beside it, so a reload
  // keeps it and the view can be linked to. An id that is not one of this
  // organizer's events is ignored rather than shown: a hand-edited or stale
  // link must not put another tenant's event id on screen.
  const asked = Number(params.get('event')) || null;
  const askedActivity = params.get('activity') || ALL_ACTIVITIES;
  const eventId = desk.events.some((row) => row.id === asked)
    ? asked
    : defaultEventId(groups);
  const event = desk.events.find((row) => row.id === eventId) || null;
  // const listIsAll = filter === 'all' && !passcode;

  // The activity picked in the URL, checked against this event's activities.
  // An activity id that is not in this event's list falls back to All —
  // switching events leaves a stale ?activity= behind, and a picker showing
  // another event's activity would be worse than resetting to the whole event.
  // (Worked out here, before the list is read, because the list follows it.)
  const { breakdownQuery, breakdown } = useActivityBreakdown(eventId);
  const activities = breakdown.activities;
  const scopedTo = activities.find((a) => String(a.id) === String(askedActivity)) || null;

  // The participant list follows the picked activity too. A pass does not
  // carry its activity, its registration does: so one read for that
  // activity's registration ids, then the usual pass read narrowed with
  // `registration_id IN (…)`. A SEQUENTIAL event leaves every registration's
  // activity empty on purpose (one entry covers every leg), so there the list
  // keeps showing the whole event, as it always has.
  const sequential = String(event?.activity_mode || '').toUpperCase() === 'SEQUENTIAL';
  const listActivityId = scopedTo && !sequential ? scopedTo.id : null;
  // The URL names an activity the breakdown has not answered for yet: nothing
  // that depends on "whole event or one activity?" is read until it has —
  // otherwise the whole event is read first and thrown away. A failed
  // breakdown ends the wait and falls back to the whole event.
  const awaitingActivity = askedActivity !== ALL_ACTIVITIES && breakdownQuery.isLoading;
  // Every read below is narrowed to CONFIRMED registrations — the whole
  // event's, or the picked activity's. A cancelled registration keeps its
  // ISSUED pass, so without this it is counted as Registered and Not arrived.
  const confirmedRegs = useQuery({
    queryKey: ['checkInHistory', eventId, 'confirmedRegs', listActivityId],
    queryFn: () => fetchConfirmedRegistrationIds(checkInApi, eventId, listActivityId),
    enabled: eventId != null && !awaitingActivity,
    staleTime: Infinity,
  });
  const registrationIds = confirmedRegs.data;
  // Part of the list's and the count's keys: when Refresh finds a new
  // registration for the activity, the changed ids are a new key, so the list
  // is read again with them instead of keeping the page read with the old ids.
  const regsKey = registrationIds ? registrationIds.join(',') : null;
  // "All" for the event: the unfiltered list's total doubles as Registered.
  const listIsAll = filter === 'all' && !passcode && listActivityId == null;

  const list = usePassPages({
    queryKey: ['checkInHistory', eventId, 'list', filter, passcode, listActivityId, regsKey],
    eventIds: eventId == null ? [] : [eventId],
    statuses: PASS_STATUSES[filter],
    search: passcode,
    registrationIds,
    withTimes: true,
    // The list waits for the confirmed registrations it is narrowed to (whose
    // read itself waits while the URL names an activity the breakdown has not
    // answered for yet — otherwise the whole event is read first and flashes
    // before switching). A failed breakdown stops the wait and falls back to
    // the whole event, as an unknown activity does.
    enabled: registrationIds != null && !awaitingActivity,
  });
  // The picked activity's passes that are not cancelled — the "All" chip when
  // the list is narrowed. One COUNT(*), read only while an activity is picked.
  // While the narrowed list itself shows All with no search, its own total IS
  // this number — the same way the whole event's list total doubles as
  // Registered — so the COUNT(*) is read only once a chip or a search
  // narrows the list further.
  const listShowsAll = filter === 'all' && !passcode;
  const activityAll = useQuery({
    queryKey: ['checkInHistory', eventId, 'activityAll', listActivityId, regsKey],
    queryFn: () => countActivityPasses(checkInApi, [eventId], PASS_STATUSES.all, registrationIds),
    // enabled: eventId != null && listActivityId != null && registrationIds != null,
    enabled: eventId != null && listActivityId != null && registrationIds != null && !listShowsAll,
    staleTime: Infinity,
  });
  // const { totals, counts, registered } = useEventTotals(eventId, list, listIsAll);
  const { totals, counts, registered } = useEventTotals(
    eventId, list, listIsAll, registrationIds, regsKey, listActivityId == null && !awaitingActivity,
  );
  // const { breakdownQuery, breakdown } = useActivityBreakdown(eventId);
  const rows = pageRows(list.data);
  // breakdownQuery.isError is deliberately absent — see useActivityBreakdown.
  const failed = list.isError || counts.isError || registered.isError
    || confirmedRegs.isError || activityAll.isError;
  const refreshing = (list.isFetching && !list.isFetchingNextPage) || counts.isFetching;
  const refresh = () => {
    // list.refetch(); counts.refetch(); breakdownQuery.refetch();
    // if (!listIsAll) registered.refetch();
    // refetch() runs even a disabled query, so the whole-event counts are
    // refreshed only when they are on screen (see useEventTotals `needed`).
    // A registration confirmed or cancelled since the last read changes the
    // ids, which are part of every count's key, so those re-read with them.
    list.refetch(); breakdownQuery.refetch(); confirmedRegs.refetch();
    if (listActivityId == null) {
      counts.refetch();
      if (!listIsAll) registered.refetch();
    }
    if (listActivityId != null && !listShowsAll) activityAll.refetch();
  };

  if (desk.isLoading) {
    return <div className="org-content"><div className="org-empty"><p>Loading your events…</p></div></div>;
  }
  if (desk.isError) {
    return (
      <div className="org-content">
        <div className="org-empty" role="alert">
          <h2>Couldn’t load your events</h2>
          <button type="button" className="org-btn" style={{ marginTop: 14 }} onClick={desk.refetch}>Retry</button>
        </div>
      </div>
    );
  }
  if (eventId == null) {
    return (
      <div className="org-content">
        <div className="org-empty"><h2>No events yet</h2><p>Results show here once you have a published event.</p></div>
      </div>
    );
  }

  // Moved above the list read, which now follows the picked activity:
  // const activities = breakdown.activities;
  // const scopedTo = activities.find((a) => String(a.id) === String(askedActivity)) || null;

  // Every tile reads from ONE object. With an activity picked that is its row;
  // with All it is the event-wide counts the page already had — so the two
  // views cannot drift into two different definitions of "registered".
  const figures = scopedTo
    ? {
      registered: scopedTo.registrations,
      confirmed: scopedTo.confirmed,
      checkedIn: scopedTo.checkedIn,
      checkedOut: scopedTo.checkedOut,
      stillIn: Math.max(0, scopedTo.checkedIn - scopedTo.checkedOut),
      notArrived: Math.max(0, scopedTo.registrations - scopedTo.checkedIn),
      capacity: scopedTo.capacity,
      cappedCount: scopedTo.capacity == null ? 0 : 1,
      activityCount: 1,
      expected: scopedTo.expected,
      priced: scopedTo.fee > 0,
    }
    : {
      ...totals,
      confirmed: breakdown.totals.confirmed,
      capacity: breakdown.totals.capacity,
      cappedCount: breakdown.totals.cappedCount,
      activityCount: activities.length,
      expected: breakdown.totals.expected,
      priced: breakdown.totals.priced,
    };

  // Previously WHOLE-EVENT counts only, because the list was never narrowed:
  // const countOf = totals ? {
  //   all: totals.registered,
  //   checked_in: totals.checkedIn,
  //   checked_out: totals.checkedOut,
  //   not_arrived: totals.notArrived,
  // } : {};
  // The list now follows the picked activity (see listActivityId), so its
  // chips count that activity: All from one COUNT(*) of its passes, checked
  // in and out from its breakdown row — the same numbers as its tiles. For a
  // SEQUENTIAL event, or with no activity picked, the list is the whole event
  // and so are the counts.
  // const activityAllCount = activityAll.data;
  const activityAllCount = listShowsAll ? list.data?.pages[0]?.count : activityAll.data;
  // The page can draw once the numbers THIS view shows have arrived: the
  // picked activity's pass count when the list follows an activity (its tiles
  // come from the breakdown row), else the whole-event totals.
  const ready = listActivityId != null ? activityAllCount != null : Boolean(totals);
  const countOf = listActivityId != null
    ? (activityAllCount == null ? {} : {
      all: activityAllCount,
      checked_in: scopedTo.checkedIn,
      checked_out: scopedTo.checkedOut,
      not_arrived: Math.max(0, activityAllCount - scopedTo.checkedIn),
    })
    : (totals ? {
      all: totals.registered,
      checked_in: totals.checkedIn,
      checked_out: totals.checkedOut,
      not_arrived: totals.notArrived,
    } : {});
  const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);
  const tz = event?.timezone;
  // Gated on having rows, NOT on the query's current status: a background
  // refetch that fails must not make numbers the organizer is already reading
  // disappear. No rows means the question is still unanswered, and an
  // unanswered question must not render as "No cap", which is an answer.
  const showCapacity = activities.length > 0;
  // An event can DECLARE activities the query returns none of — their rows
  // carry no owner, so row-level security filters every one away. The
  // organizer picked this event and can see it, so a tile that simply
  // disappears reads as a missing feature. Say the number is not available
  // instead, and leave the fix to the ownership-relation health check.
  const declaredActivities = Number(event?.number_of_activities) || 0;
  const capacityWithheld = !showCapacity
    && breakdownQuery.isSuccess
    && declaredActivities > 0;
  const activityCount = `${figures.activityCount} ${figures.activityCount === 1 ? 'activity' : 'activities'}`;
  // "% full" is only honest when the total covers every activity. With one
  // capped 10K and an uncapped Fun Run, registrations from both divided by the
  // 10K's cap alone reads well over 100% — a number about a denominator the
  // event does not have.
  const everyActivityCapped = figures.cappedCount === figures.activityCount;
  const capacityText = figures.capacity == null ? 'No cap' : figures.capacity.toLocaleString();
  // Shown when the ACTIVITY IS PRICED — fee_amount > 0 — not when the revenue
  // happens to be non-zero. A paid activity nobody has confirmed yet is worth
  // a tile reading zero; a free one is not, and a zero revenue alone cannot
  // tell those two apart, which is why the query returns the fee as well.
  //
  // Not gated on `is_payment_required`: that flag disagrees with the fees in
  // real data — Coastal Ultra is flagged free while its activities carry 750 —
  // and the fee is the number an organizer actually set.
  const showRevenue = Boolean(figures.priced);
  // Whether the comparison table carries a money column at all.
  const anyPriced = breakdown.totals.priced;
  // The rate behind the gross figure, when there IS a single rate to name.
  // With one activity selected that is its fee; on the All view only when
  // every priced activity charges the same, because naming one of several
  // different fees would misdescribe the sum sitting above it.
  const distinctFees = [...new Set(activities.filter((a) => a.fee > 0).map((a) => a.fee))];
  const basisFee = scopedTo
    ? (scopedTo.fee > 0 ? scopedTo.fee : null)
    : (distinctFees.length === 1 ? distinctFees[0] : null);

  /** Narrow the page to one activity, or back to all of them. */
  const selectActivity = (id) => {
    const next = { view: 'history' };
    if (eventId != null) next.event = String(eventId);
    // All is the default, so it stays out of the URL rather than parking
    // `activity=all` on every link.
    if (id !== ALL_ACTIVITIES) next.activity = String(id);
    setParams(next, { replace: true });
  };
  // A mistyped cap (999999999) is nine digits plus separators, which runs past
  // the tile at the shared 32px. Clipping a number is worse than setting it a
  // size smaller, and only these outliers pay for it.
  const capacityIsLong = capacityText.length > 9;

  return (
    <div className="org-content ochh">
      <div className="ochh-bar">
        <EventPicker
          groups={groups}
          selected={event}
          onSelect={(id) => {
            // `view` is carried through deliberately: setParams replaces the
            // whole query string, so omitting it would drop the History tab.
            // No `activity`: another event's activity id means nothing here.
            setParams({ view: 'history', event: String(id) }, { replace: true });
            setFilter('all');
            setSearch('');
          }}
        />
        {hasChoice(activities) && (
          <ActivityPicker
            activities={activities}
            selected={scopedTo ? scopedTo.id : ALL_ACTIVITIES}
            totalCapacity={breakdown.totals.capacity}
            onSelect={selectActivity}
          />
        )}
        <button
          type="button"
          className="ochk-iconbtn"
          onClick={refresh}
          aria-label="Refresh results"
          disabled={refreshing}
        >
          <i className={`bi bi-arrow-clockwise${refreshing ? ' ochk-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {failed && (
        <div className="org-empty" role="alert">
          <h2>Couldn’t load the results</h2>
          <button type="button" className="org-btn" style={{ marginTop: 14 }} onClick={refresh}>Retry</button>
        </div>
      )}
      {/* {!failed && !totals && <div className="org-empty"><p>Loading results…</p></div>} */}
      {!failed && !ready && <div className="org-empty"><p>Loading results…</p></div>}

      {/* {totals && ( */}
      {ready && (
        <>
          {scopedTo && (
            <p className="ochh-scopenote" data-testid="ochh-scope-note">
              Showing <b>{scopedTo.name}</b> &mdash; the figures below cover this activity only.
            </p>
          )}

          <div className="ochh-tiles">
            {capacityWithheld && (
              <div className="ochh-tile ochh-cap" data-testid="ochh-capacity">
                <span className="org-lab">Capacity</span>
                <b className="org-num ochh-capna">&mdash;</b>
                <span>Not visible to you</span>
              </div>
            )}
            {showCapacity && (
              <div className="ochh-tile ochh-cap" data-testid="ochh-capacity">
                <span className="org-lab">Capacity</span>
                <b className={`org-num${capacityIsLong ? ' is-long' : ''}`}>{capacityText}</b>
                <span>
                  {figures.capacity == null
                    ? `${activityCount} with no limit set`
                    : everyActivityCapped
                      ? `${pct(figures.registered, figures.capacity)}% full · ${activityCount}`
                      : `${figures.cappedCount} of ${figures.activityCount} capped`}
                </span>
              </div>
            )}
            <div className="ochh-tile">
              <span className="org-lab">Registered</span><b className="org-num">{figures.registered}</b>
              {scopedTo && <span>{figures.confirmed} confirmed</span>}
            </div>
            <div className="ochh-tile is-in">
              <span className="org-lab">Checked in</span><b className="org-num">{figures.checkedIn}</b>
              <span>{pct(figures.checkedIn, figures.registered)}% of registered</span>
            </div>
            <div className="ochh-tile">
              <span className="org-lab">Checked out</span><b className="org-num">{figures.checkedOut}</b>
              <span>{figures.stillIn} still checked in</span>
            </div>
            <div className="ochh-tile is-none">
              <span className="org-lab">Not arrived</span><b className="org-num">{figures.notArrived}</b>
              <span>{pct(figures.notArrived, figures.registered)}% no-show</span>
            </div>
            {showRevenue && (
              <div className="ochh-tile ochh-exp" data-testid="ochh-revenue">
                <span className="org-lab">Gross revenue</span>
                <b className="org-num">{figures.expected.toLocaleString()}</b>
                {/* GROSS, and only from CONFIRMED registrations: fee x
                    confirmed. It is not money banked — a payment-first
                    checkout can take money against a registration that never
                    reaches CONFIRMED, and money sits against cancelled ones —
                    so the collected figure stays in the payment analytics. */}
                <span>
                  {basisFee == null
                    ? `from ${figures.confirmed} confirmed`
                    : `${basisFee.toLocaleString()} × ${figures.confirmed} confirmed`}
                </span>
              </div>
            )}
          </div>

          {showCapacity && !scopedTo && activities.length > 1 && (
            <div className="ochh-actwrap">
              <table className="ochh-actable" data-testid="ochh-activity-table">
                <caption className="sr-only-org">
                  Every activity of this event, side by side. Select a row to narrow the page to it.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Activity</th>
                    <th scope="col">Capacity</th>
                    <th scope="col">Registered</th>
                    <th scope="col">Checked in</th>
                    <th scope="col">Checked out</th>
                    {anyPriced && <th scope="col">Gross revenue</th>}
                  </tr>
                </thead>
                <tbody>
                  {activities.map((row) => (
                    // Clickable, because the table and the picker choose the
                    // same thing — reading a row and then hunting the dropdown
                    // for the name you just read is a pointless second step.
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`Show only ${row.name}`}
                      onClick={() => selectActivity(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          selectActivity(row.id);
                        }
                      }}
                    >
                      <td data-label="Activity">
                        <b className="ochk-name" title={row.name}>{row.name}</b>
                      </td>
                      <td data-label="Capacity" className="org-num">
                        {row.capacity == null ? 'No cap' : row.capacity.toLocaleString()}
                      </td>
                      <td data-label="Registered" className="org-num">{row.registrations}</td>
                      <td data-label="Checked in" className="org-num">{row.checkedIn}</td>
                      <td data-label="Checked out" className="org-num">{row.checkedOut}</td>
                      {anyPriced && (
                        <td data-label="Gross revenue" className="org-num">
                          {row.fee > 0 ? row.expected.toLocaleString() : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <section className="ochh-people" aria-label="Participants">
            {/* Says, on the section it describes, whose rows these are: the
                whole event, or only the picked activity. A SEQUENTIAL event's
                list is always the whole event (see listActivityId). */}
            <div className="ochh-peoplehead" data-testid="ochh-people-head">
              <h3>All event details</h3>
              {listActivityId != null
                ? <p>Participants who chose <b>{scopedTo.name}</b>.</p>
                : <p>Every participant of this event, whichever activity they chose.</p>}
            </div>
            <div className="ochh-peoplebar">
              <div className="ochk-filters" role="group" aria-label="Filter participants">
                {FILTERS.map((item) => (
                  <button key={item.id} type="button" className="ochk-filter" aria-pressed={filter === item.id} onClick={() => setFilter(item.id)}>
                    {item.label}{!passcode && <span>{countOf[item.id]}</span>}
                  </button>
                ))}
              </div>
              <label className="sr-only-org" htmlFor="ochh-people-search">Search by passcode</label>
              <input
                id="ochh-people-search"
                className="orgf-input ochh-peoplesearch"
                type="search"
                placeholder="Search by passcode"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {/* Exports exactly what the list shows: same status chip,
                  passcode search and activity. */}
              <ExportParticipants
                eventId={eventId}
                eventName={event?.title || ''}
                statuses={PASS_STATUSES[filter]}
                search={passcode}
                registrationIds={registrationIds}
                // Names the file for what it holds, e.g. "(Checked in, Coastal 21K)".
                scopeLabels={[
                  filter !== 'all' ? FILTERS.find((f) => f.id === filter)?.label : null,
                  listActivityId != null ? scopedTo.name : null,
                  passcode ? `passcode ${passcode}` : null,
                ]}
                disabled={registrationIds == null}
                empty={list.isSuccess && rows.length === 0}
              />
            </div>

            {list.isLoading && <p className="ochk-recentmsg">Loading participants…</p>}
            {list.isSuccess && rows.length === 0 && (
              <p className="ochk-recentmsg">
                {passcode ? 'No participant has that passcode.' : 'Nobody here.'}
              </p>
            )}
            {rows.length > 0 && (
              <table className="ochh-table">
                <thead>
                  <tr>
                    <th scope="col">Participant</th>
                    <th scope="col">Checked in</th>
                    <th scope="col">Checked out</th>
                    <th scope="col">Status</th>
                    <th scope="col"><span className="sr-only-org">Details</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const status = ROW_STATUS[row.status] || ROW_STATUS.ISSUED;
                    return (
                      <tr key={row.passId}>
                        <td data-label="Participant">
                          <b className="ochk-name">{row.name || row.passcode || '—'}</b>
                          {row.name && row.passcode && <span className="ochk-code ochh-subcode">{row.passcode}</span>}
                        </td>
                        <td data-label="Checked in" className="org-num">{row.inAt ? clockIn(row.inAt, tz) : '—'}</td>
                        <td data-label="Checked out" className="org-num">
                          {row.status === 'CHECKED_OUT' && row.outAt ? clockIn(row.outAt, tz) : '—'}
                        </td>
                        <td data-label="Status">
                          <span className={`ochh-status is-${status.tone}`}>{status.label}</span>
                        </td>
                        <td data-label="Details" className="ochh-eyecell">
                          <button
                            type="button"
                            className="ochk-iconbtn ochh-eye"
                            onClick={() => setShown(row)}
                            aria-label={`Details for ${row.name || row.passcode}`}
                          >
                            <i className="bi bi-eye" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {shown && (
              <ParticipantDetails row={shown} timezone={tz} onClose={() => setShown(null)} />
            )}
            {list.hasNextPage && (
              <button
                type="button"
                className="org-btn ghost ochk-more"
                onClick={() => list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
              >
                {list.isFetchingNextPage ? 'Loading…' : `Show more · ${rows.length} of ${list.data.pages[0].count}`}
              </button>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default CheckInHistory;
